import EmptyLeftSidebarComponent from "./components/leftSideBar/none";
import NewsfeedNavbarSection from "./components/leftSideBar/newsfeed/navbar";
import NewsfeedBodySection from "./components/leftSideBar/newsfeed/body";
import NewsfeedFooterSection from "./components/leftSideBar/newsfeed/footer";
import GivingBannerSection from "./components/leftSideBar/giving/banner";
import GivingBodySection from "./components/leftSideBar/giving/body";
import GivingSideParagraphSection from "./components/leftSideBar/giving/sideParagraph";
import EmailBodySection from "./components/leftSideBar/email/body";
import EmailSocialsSection from "./components/leftSideBar/email/socials";
import EmailFooterSection from "./components/leftSideBar/email/footer";
import PostDetailMainSection from "./components/leftSideBar/postDetail/main";
import PostDetailBioSection from "./components/leftSideBar/postDetail/bio";

export const POST_DETAIL_LAYOUT = {
  CENTERED: "centered",
  LATEST_NEWS: "latestNews",
  BIO: "bio",
} as const;

export type PostDetailLayoutValue =
  (typeof POST_DETAIL_LAYOUT)[keyof typeof POST_DETAIL_LAYOUT];

export const PAGES = {
  NEWSFEED_PAGE: "Newsfeed Page",
  GIVING_PAGE: "Giving Page",
  POST_DETAILS_PAGE: "Post Details",
};

export const PAGES_PARSING = {
  [PAGES.NEWSFEED_PAGE]: "preview",
  [PAGES.GIVING_PAGE]: "donate",
  [PAGES.POST_DETAILS_PAGE]: "post?id=preview",
};

export const GIVING_SECTION_IDS = new Set([
  "delta-header",
  "donate-body",
  "delta-sidebar",
]);

export const NEWSFEED_SECTION_IDS = new Set(["header", "news-feed", "footer"]);

export const POST_DETAIL_SECTION_IDS = new Set([
  "post-detail-main",
  "post-detail-bio",
]);

export function getPostDetailSectionIds(layout: string): Set<string> {
  const ids = new Set<string>(["post-detail-main"]);
  if (layout === POST_DETAIL_LAYOUT.BIO) {
    ids.add("post-detail-bio");
  }
  return ids;
}

export const FIELD_NAME_MAP = {
  "header_.logo-web_style_background": "newsfeedNavbarLogo",
  "header_.charity-name_text": "newsfeedHeaderTitle",
  "header_.give-now-button-nav_text": "newsfeedNavbarGiveButtonLabel",
  "header_.give-now-button-nav_style_background-color,border-color":
    "newsfeedNavbarGiveButtonColor",
  "header_.headerV2_style_border-bottom-color": "newsfeedNavbarHighlightColor",
  "header_.navbar-toggle_style_border-color": "newsfeedNavbarToggleColor",
  "header_.icon-bar_style_background-color": "newsfeedNavbarToggleBarColor",

  "news-feed_.title a_style_color": "newsfeedBodyHighlightedTextColor",
  "news-feed_.give-now-button_text": "newsfeedBodyGiveButtonLabel",
  "news-feed_.give-now-button_style_background-color,border-color":
    "newsfeedBodyGiveButtonColor",
  "news-feed_.post-list .actions .share span_style_color":
    "newsfeedBodyShareColor",

  "footer_.footer-first-part_text": "newsfeedFooterFirstPart",
  "footer_.footer-second-part_text": "newsfeedFooterSecondPart",
  "footer_.version_text": "newsfeedFooterVersion",

  "donate-body_.headers_style_background-color":
    "givingBodyHeadersAndAmountColor",
  "donate-body_.active.selected-amount_style_background-color,border-color":
    "givingBodySelectedAmountColor",
  "donate-body_.underLine_style_border-color":
    "givingBodySelectedFrequencyColor",
  "donate-body_.arrow-up.arrowActive_style_color": "givingBodyArrowColor",
  "donate-body_.inactive_style_background-color": "givingBodyInactiveTabColor",
  "donate-body_.text.one-time_text": "givingBodyOneTimeMessageText",
  "donate-body_.text.monthly_text": "givingBodyMonthlyMessageText",
  "donate-body_.amounts .btnCol:nth-child(1) button_text": "givingBodyAmount1",
  "donate-body_.amounts .btnCol:nth-child(2) button_text": "givingBodyAmount2",
  "donate-body_.amounts .btnCol:nth-child(3) button_text": "givingBodyAmount3",
  "donate-body_.amounts .btnCol:nth-child(4) button_text": "givingBodyAmount4",

  "delta-header_.topBanner_style_background-image": "givingBannerImageDesktop",
  "delta-header_.topBanner_style_background-image_(max-width: 992px)":
    "givingBannerImageMobile",
  "delta-header_.topBanner_style_color": "givingBannerOverlayColor",
  "delta-header_.topBanner_style_box-shadow": "givingBannerOverlayDisplay",
  "delta-header_.first_text": "givingBannerMainMessageDesktop",
  "delta-header_.middleBanner .first_text": "givingBannerMainMessageMobile",
  "delta-header_.first_style_color": "givingBannerMainMessageDesktopColor",
  "delta-header_.middleBanner .first_style_color":
    "givingBannerMainMessageMobileColor",
  "delta-header_.topBanner .second_text": "givingBannerSecondaryMessageDesktop",
  "delta-header_.middleBanner .second_text":
    "givingBannerSecondaryMessageMobile",
  "delta-header_.topBanner .second_style_color":
    "givingBannerSecondaryMessageDesktopColor",
  "delta-header_.middleBanner .second_style_color":
    "givingBannerSecondaryMessageMobileColor",
  "delta-header_.first_style_text-align": "givingBannerMainMessageAlignment",
  "delta-header_.first_style_text-transform":
    "givingBannerMainMessageTransform",
  "delta-header_.topBanner .second_style_text-align":
    "givingBannerSecondaryMessageAlignment",

  "delta-sidebar_.second.side-paragraph_html": "givingSideParagraphText",
  "delta-sidebar_.second_style_color": "givingSideParagraphTextColor",
  "delta-sidebar_.rightText .second strong_style_color":
    "givingSideParagraphStrongTextColor",

  "post-detail-main_.give-now-button-details_text": "postDetailGiveButtonLabel",
  "post-detail-bio_.prime-paragraph-description_html": "postDetailBioDescription",
  "post-detail-bio_.prime-image-description_style_background-image":
    "postDetailBioImage",
};

export const SECTIONS = {
  NONE: {
    id: "none",
    component: <EmptyLeftSidebarComponent />,
  },
  NEWSFEED_NAVBAR: {
    id: "header",
    title: "Navbar section",
    helpText: "Customize the navbar section of the newsfeed.",
    component: <NewsfeedNavbarSection />,
  },
  NEWSFEED_BODY: {
    id: "news-feed",
    title: "Body section",
    helpText: "Customize the body section of the newsfeed.",
    component: <NewsfeedBodySection />,
  },
  NEWSFEED_FOOTER: {
    id: "footer",
    title: "Footer section",
    helpText:
      "Customize the footer section of the newsfeed. Whenever you need to setup the year, use the word YEAR and it'll be automatically replaced with the current year.",
    component: <NewsfeedFooterSection />,
  },
  GIVING_BANNER: {
    id: "delta-header",
    title: "Giving Banner",
    helpText: "Customize the giving banner section.",
    component: <GivingBannerSection />,
  },
  GIVING_BODY: {
    id: "donate-body",
    title: "Donation Tabs",
    helpText: "Customize the body section of the giving page.",
    component: <GivingBodySection />,
  },
  GIVING_SIDE_PARAGRAPH: {
    id: "delta-sidebar",
    title: "Side Paragraph Section",
    helpText: "Customize the side paragraph of the giving page.",
    component: <GivingSideParagraphSection />,
  },
  EMAIL_BODY: {
    id: "EMAIL_BODY",
    title: "Email Body",
    helpText: "Customize the body section of the email.",
    component: <EmailBodySection />,
  },
  EMAIL_SOCIALS: {
    id: "EMAIL_SOCIALS",
    title: "Social Media Section",
    helpText: "Customize the social media links of the email.",
    component: <EmailSocialsSection />,
  },
  EMAIL_FOOTER: {
    id: "EMAIL_FOOTER",
    title: "Email Footer",
    helpText: "Customize the footer section of the email.",
    component: <EmailFooterSection />,
  },
  POST_DETAIL_MAIN: {
    id: "post-detail-main",
    title: "Post & Give",
    helpText: "Customize the Give button label and post title casing.",
    component: <PostDetailMainSection />,
  },
  POST_DETAIL_BIO: {
    id: "post-detail-bio",
    title: "Bio",
    helpText: "Customize the bio image and description.",
    component: <PostDetailBioSection />,
  },
};
